import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../utils/api';

const AccountSettings = () => {
  const { user, updateUser } = useAuth();

  // Profile state
  const [email, setEmail] = useState(user?.email || '');
  const [fullName, setFullName] = useState(user?.fullName || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Email notification state (HQ only)
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationError, setNotificationError] = useState('');
  const [notificationSuccess, setNotificationSuccess] = useState('');

  // Check if user is HQ (staff/superuser)
  const isHQ = user?.isStaff || user?.isSuperuser;

  // Load email notification settings for HQ users
  useEffect(() => {
    const loadNotificationSettings = async () => {
      if (!isHQ) return;

      try {
        const res = await authAPI.getEmailNotifications();
        setEmailNotifications(res.data.data.emailNotifications);
      } catch (err) {
        console.error('Error loading notification settings:', err);
      }
    };

    loadNotificationSettings();
  }, [isHQ]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setProfileLoading(true);

    try {
      const res = await authAPI.updateProfile({ email, fullName });
      setProfileSuccess(res.data.message);

      // Update user in context
      updateUser(res.data.data);

      // Store updated user in localStorage
      localStorage.setItem('user', JSON.stringify(res.data.data));
    } catch (err) {
      setProfileError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการอัพเดทข้อมูล');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validate
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('รหัสผ่านใหม่ต้องมีความยาวอย่างน้อย 6 ตัวอักษร');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('รหัสผ่านใหม่และยืนยันรหัสผ่านไม่ตรงกัน');
      return;
    }

    setPasswordLoading(true);

    try {
      const res = await authAPI.changePassword({ currentPassword, newPassword });
      setPasswordSuccess(res.data.message);

      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    setNotificationError('');
    setNotificationSuccess('');
    setNotificationLoading(true);

    try {
      const res = await authAPI.updateEmailNotifications({ emailNotifications });
      setNotificationSuccess(res.data.message);
    } catch (err) {
      setNotificationError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการอัพเดทการตั้งค่า');
    } finally {
      setNotificationLoading(false);
    }
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-person-circle me-2"></i>
            ตั้งค่าบัญชี
          </h2>
          <p className="text-muted">จัดการข้อมูลส่วนตัวและรหัสผ่านของคุณ</p>
        </Col>
      </Row>

      <Row>
        {/* Profile Information */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="bi bi-person-badge me-2"></i>
                ข้อมูลส่วนตัว
              </h5>
            </Card.Header>
            <Card.Body>
              {profileError && (
                <Alert variant="danger" dismissible onClose={() => setProfileError('')}>
                  {profileError}
                </Alert>
              )}
              {profileSuccess && (
                <Alert variant="success" dismissible onClose={() => setProfileSuccess('')}>
                  {profileSuccess}
                </Alert>
              )}

              <Form onSubmit={handleProfileSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="bi bi-person me-1"></i>
                    ชื่อผู้ใช้
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={user?.username || ''}
                    disabled
                    readOnly
                  />
                  <Form.Text className="text-muted">
                    ชื่อผู้ใช้ไม่สามารถเปลี่ยนแปลงได้
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="bi bi-envelope me-1"></i>
                    อีเมล
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="กรุณากรอกอีเมล"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="bi bi-card-text me-1"></i>
                    ชื่อเต็ม
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="กรุณากรอกชื่อเต็ม"
                  />
                </Form.Group>

                <div className="d-grid">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={profileLoading}
                  >
                    {profileLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        กำลังบันทึก...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        บันทึกข้อมูล
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Change Password */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Header className="bg-warning text-dark">
              <h5 className="mb-0">
                <i className="bi bi-key me-2"></i>
                เปลี่ยนรหัสผ่าน
              </h5>
            </Card.Header>
            <Card.Body>
              {passwordError && (
                <Alert variant="danger" dismissible onClose={() => setPasswordError('')}>
                  {passwordError}
                </Alert>
              )}
              {passwordSuccess && (
                <Alert variant="success" dismissible onClose={() => setPasswordSuccess('')}>
                  {passwordSuccess}
                </Alert>
              )}

              <Form onSubmit={handlePasswordSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="bi bi-lock me-1"></i>
                    รหัสผ่านปัจจุบัน
                  </Form.Label>
                  <Form.Control
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="กรุณากรอกรหัสผ่านปัจจุบัน"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="bi bi-lock-fill me-1"></i>
                    รหัสผ่านใหม่
                  </Form.Label>
                  <Form.Control
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="กรุณากรอกรหัสผ่านใหม่ (อย่างน้อย 6 ตัวอักษร)"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="bi bi-shield-check me-1"></i>
                    ยืนยันรหัสผ่านใหม่
                  </Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="กรุณากรอกรหัสผ่านใหม่อีกครั้ง"
                  />
                </Form.Group>

                <div className="d-grid">
                  <Button
                    type="submit"
                    variant="warning"
                    disabled={passwordLoading}
                  >
                    {passwordLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        กำลังเปลี่ยนรหัสผ่าน...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-key-fill me-2"></i>
                        เปลี่ยนรหัสผ่าน
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Email Notification Settings (HQ only) */}
      {isHQ && (
        <Row className="mb-4">
          <Col md={12}>
            <Card>
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0">
                  <i className="bi bi-bell me-2"></i>
                  การตั้งค่าการแจ้งเตือนทางอีเมล
                </h5>
              </Card.Header>
              <Card.Body>
                {notificationError && (
                  <Alert variant="danger" dismissible onClose={() => setNotificationError('')}>
                    {notificationError}
                  </Alert>
                )}
                {notificationSuccess && (
                  <Alert variant="success" dismissible onClose={() => setNotificationSuccess('')}>
                    {notificationSuccess}
                  </Alert>
                )}

                <Form onSubmit={handleNotificationSubmit}>
                  <div className="mb-3">
                    <Alert variant="info">
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>เกี่ยวกับการแจ้งเตือนทางอีเมล</strong>
                      <p className="mb-0 mt-2">
                        เมื่อเปิดใช้งาน คุณจะได้รับอีเมลแจ้งเตือนเมื่อ:
                      </p>
                      <ul className="mb-0 mt-1">
                        <li>มีคำสั่งซื้อใหม่จากสาขา</li>
                        <li>คำสั่งซื้อได้รับการอนุมัติ</li>
                        <li>คำสั่งซื้อถูกปรับสถานะ</li>
                      </ul>
                    </Alert>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="email-notifications-switch"
                      label={
                        <span>
                          <strong>
                            {emailNotifications ? (
                              <>
                                <i className="bi bi-bell-fill text-success me-2"></i>
                                เปิดการแจ้งเตือนทางอีเมล
                              </>
                            ) : (
                              <>
                                <i className="bi bi-bell-slash text-muted me-2"></i>
                                ปิดการแจ้งเตือนทางอีเมล
                              </>
                            )}
                          </strong>
                        </span>
                      }
                      checked={emailNotifications}
                      onChange={(e) => setEmailNotifications(e.target.checked)}
                    />
                    <Form.Text className="text-muted">
                      {emailNotifications
                        ? 'คุณจะได้รับอีเมลแจ้งเตือนเมื่อมีกิจกรรมที่เกี่ยวข้อง'
                        : 'คุณจะไม่ได้รับอีเมลแจ้งเตือนใดๆ'}
                    </Form.Text>
                  </Form.Group>

                  <div className="d-grid">
                    <Button
                      type="submit"
                      variant="success"
                      disabled={notificationLoading}
                    >
                      {notificationLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          กำลังบันทึก...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-save me-2"></i>
                          บันทึกการตั้งค่า
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Account Information */}
      <Row>
        <Col md={12}>
          <Card>
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">
                <i className="bi bi-info-circle me-2"></i>
                ข้อมูลบัญชี
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p className="mb-2">
                    <strong>ชื่อผู้ใช้:</strong> {user?.username}
                  </p>
                  <p className="mb-2">
                    <strong>ประเภทผู้ใช้:</strong>{' '}
                    {user?.isSuperuser ? (
                      <Badge bg="danger">Superuser</Badge>
                    ) : user?.isStaff ? (
                      <Badge bg="warning" text="dark">Staff</Badge>
                    ) : (
                      <Badge bg="primary">Branch</Badge>
                    )}
                  </p>
                </Col>
                <Col md={6}>
                  <p className="mb-2">
                    <strong>อีเมล:</strong> {user?.email || '-'}
                  </p>
                  <p className="mb-2">
                    <strong>ชื่อเต็ม:</strong> {user?.fullName || '-'}
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AccountSettings;
